import React, { useState, useMemo, useCallback, memo, useEffect, createContext, useContext } from 'react';
import { 
  Box, 
  Typography, 
  Tabs, 
  Tab, 
  TextField, 
  Tooltip, 
} from '@mui/material';
import { getSortedSeeds, debounce, loadInventory, saveInventory } from '../components/InventoryDatabase/InventoryDatabase';
import { allAnimals } from '../components/AnimalDatabase/AnimalDatabase';
import RarityAvatar from '../components/common/RarityAvatar';
import RarityBadge from '../components/common/RarityBadge';
import { BED_DATA } from './Beds';
import { tabStyles, avatarStyles } from '../theme/componentStyles';
// Removed unused VirtualGrid import
import { getRarityColor } from '../utils/rarityUtils';
import { formatGrowTime, calculateBpPerMinute } from '../utils/seedUtils';

// Define rarity options at module level since they are constants
const RARITY_OPTIONS = ['All', 'Common', 'Uncommon', 'Rare', 'Epic', 'Legendary'];

const inventoryTabs = [
  {
    id: 'seeds',
    name: 'Seeds',
    image: '/images/seeds/bell_pepper/common/preview.png',
  },
  {
    id: 'beds',
    name: 'Beds',
    image: '/images/beds/basic/common/preview.png',
  },
  {
    id: 'animals',
    name: 'Animals',
    image: '/images/animals/birds/common/preview.png',
  },
];

// Memoized SeedInput component to prevent unnecessary re-renders
const SeedInput = memo(({ 
  id, 
  value, 
  onChange, 
  onKeyDown 
}) => {
  const inputSx = {
    width: '100px',
    textAlign: 'center',
    '& input[type=number]': {
      MozAppearance: 'textfield',
      WebkitAppearance: 'textfield',
      appearance: 'textfield',
      textAlign: 'center',
      padding: '8px 4px',
      '&::placeholder': {
        color: 'transparent'
      }
    },
    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
      display: 'none'
    }
  };

  return (
    <TextField
      type="number"
      value={value}
      onChange={onChange}
      onKeyDown={onKeyDown}
      inputProps={{ 
        min: 0,
        style: {
          textAlign: 'center',
          padding: '8px 4px',
          width: '100px', // Force input to 100px
          height: '24px', // Force input to 24px
        }
      }}
      size="small"
      variant="outlined"
      sx={{
        '& .MuiOutlinedInput-root': {
          height: '24px', // Force TextField to 24px
          padding: 0,
          '& fieldset': {
            textAlign: 'center'
          }
        },
        ...inputSx,
        width: '100px', // Force TextField to 100px
        height: '24px', // Force TextField to 24px
        textAlign: 'center'
      }}
    />
  );
});

// Removed unused SeedCard component

// Inventory Context and Provider
export const InventoryContext = createContext();
export function InventoryProvider({ children }) {
  const [inventory, setInventory] = useState(() => loadInventory());
  useEffect(() => { setInventory(loadInventory()); }, []);
  useEffect(() => { saveInventory(inventory); }, [inventory]);
  return (
    <InventoryContext.Provider value={{ inventory, setInventory }}>
      {children}
    </InventoryContext.Provider>
  );
}

const Inventory = () => {
  const [tabValue, setTabValue] = useState(() => {
    const hash = window.location.hash.substring(1);
    const tabIndex = inventoryTabs.findIndex(tab => tab.id === hash);
    return tabIndex >= 0 ? tabIndex : 0;
  });
  const { inventory, setInventory } = useContext(InventoryContext);
  const [seedRarityFilter, setSeedRarityFilter] = useState('All');
  const [compactView, setCompactView] = useState(false);
  
  const toggleCompactView = useCallback(() => {
    setCompactView(prev => !prev);
  }, []);
  
  // Memoize the debounced handler
  const debouncedSetInventory = useMemo(
    () => debounce((updater) => {
      setInventory(prev => {
        const updated = typeof updater === 'function' ? updater(prev) : updater;
        saveInventory(updated);
        return updated;
      });
    }, 50),
    [setInventory]
  );

  useEffect(() => {
    const savedInventory = loadInventory();
    
    // Migrate old animal keys to new standardized keys
    const migratedInventory = { ...savedInventory };
    const keyMapping = {
      'chicken': 'birds',
      'cow': 'cattle', 
      'sheep': 'fuzzies',
      'pig': 'piggies'
    };
    
    if (migratedInventory.animals) {
      const migratedAnimals = {};
      Object.entries(migratedInventory.animals).forEach(([key, count]) => {
        const [animalType, rarity] = key.split('-');
        const newAnimalType = keyMapping[animalType] || animalType;
        const newKey = `${newAnimalType}-${rarity}`;
        migratedAnimals[newKey] = count;
    });
      migratedInventory.animals = migratedAnimals;
    
    }
    
    setInventory(migratedInventory);
    
    // Listen for inventory updates from file loading
    const handleInventoryUpdate = (event) => {
      setInventory(event.detail);
    };
    
    window.addEventListener('inventoryUpdated', handleInventoryUpdate);
    
    return () => {
      window.removeEventListener('inventoryUpdated', handleInventoryUpdate);
    };
  }, [setInventory]);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.substring(1);
      const tabIndex = inventoryTabs.findIndex(tab => tab.id === hash);
      if (tabIndex >= 0 && tabIndex !== tabValue) {
        setTabValue(tabIndex);
      }
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [tabValue]);

  const handleTabChange = useCallback((event, newValue) => {
    setTabValue(newValue);
    const tabId = inventoryTabs[newValue]?.id;
    if (tabId) {
      window.location.hash = tabId;
    }
  }, []);

  const handleQuantityChange = useCallback((type, id, value) => {
    debouncedSetInventory(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [id]: value === '' ? '' : Math.max(0, parseInt(value, 10) || 0)
      }
    }));
  }, [debouncedSetInventory]);

  const handleClearSeeds = useCallback(() => {
    debouncedSetInventory(prev => ({
      ...prev,
      seeds: {}
    }));
  }, [debouncedSetInventory]);

  // Define renderSeedCard first since it's used by renderSeedCardContent
  const renderSeedCard = useCallback((seed, rarity, isCompact = false) => {
    try {
      // Basic input validation
      if (!seed || typeof seed !== 'object' || !rarity) {
        return null;
      }
      
      if (typeof rarity !== 'object') {
        return null;
      }
      
      if (!rarity.type) {
        return null;
      }
      
      // Common styles
      const containerStyles = isCompact 
        ? { 
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 0,
            height: '100%',
            boxSizing: 'border-box'
          }
        : { 
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: 2,
            p: 0
          };
      
      const avatarSize = 40;
      const avatarStyles = {
        width: avatarSize,
        height: avatarSize,
        bgcolor: 'transparent',
        mb: 1,
        '& img': { 
          objectFit: 'contain', 
          width: '100%', 
          height: '100%' 
        }
      };
      
      return (
        <Box sx={containerStyles}>
          {!isCompact ? (
            <Tooltip title={seed.name || 'Unknown Seed'} arrow placement="top">
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  textAlign: 'center',
                  fontSize: '0.9rem',
                  lineHeight: 1.2,
                  width: '100%',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  mb: 0.5,
                }}
              >
                {seed.name || 'Unknown Seed'}
              </Typography>
            </Tooltip>
          ) : (
            <Tooltip title={seed.name || 'Unknown Seed'} arrow placement="top">
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  textAlign: 'center',
                  fontSize: '0.7rem',
                  lineHeight: 1.1,
                  width: '100%',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  mb: 0.25,
                  textTransform: 'none',
                  letterSpacing: 'normal',
                  maxWidth: '80px', // Match the minWidth of the container
                }}
              >
                {seed.name || 'Unknown'}
              </Typography>
            </Tooltip>
          )}
          <Box 
            key={`${seed.id}-${rarity.type}`}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Tooltip
              title={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    {seed.name} ({rarity.type})
                  </Typography>
                  <Typography variant="body2">
                    Grow Time: {formatGrowTime(rarity.growTime)}
                  </Typography>
                  <Typography variant="body2">
                    Biopoints: {rarity.biopoints}
                  </Typography>
                  <Typography variant="body2">
                    BP/min: {calculateBpPerMinute(rarity.biopoints, rarity.growTime).toFixed(1)}
                  </Typography>
                </Box>
              }
              arrow
              placement="top"
            >
              <Box>
                <RarityAvatar
                  src={`/images/seeds/${seed.id}/${rarity.type.toLowerCase()}/preview.png`}
                  alt={`${seed.name || 'Unknown Seed'} ${rarity.type}`}
                  size={avatarSize}
                  sx={avatarStyles}
                />
              </Box>
            </Tooltip>
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mt: 0.5, mb: 0 }}>
              <RarityBadge 
                rarity={rarity.type}
                size="small"
                sx={{ 
                  width: '100px',
                  '& .MuiBadge-badge': {
                    width: '100px',
                    minWidth: '100px',
                    maxWidth: '100px',
                    boxSizing: 'border-box',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }
                }}
              />
            </Box>
            <SeedInput
              id={`${seed.id}-${rarity.type}`}
              value={
                inventory.seeds[`${seed.id}-${rarity.type}`] === undefined ||
                inventory.seeds[`${seed.id}-${rarity.type}`] === null ||
                inventory.seeds[`${seed.id}-${rarity.type}`] === ''
                  ? 0
                  : inventory.seeds[`${seed.id}-${rarity.type}`]
              }
              onChange={(e) => handleQuantityChange('seeds', `${seed.id}-${rarity.type}`, e.target.value)}
              onKeyDown={(e) => {
                if (!/^[0-9]|Backspace|Delete|Tab|ArrowLeft|ArrowRight|ArrowUp|ArrowDown$/.test(e.key) &&
                  !(e.ctrlKey && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase()))) {
                  e.preventDefault();
                }
              }}
              sx={{ 
                width: 100, 
                height: 40,
                '& .MuiOutlinedInput-root': {
                  height: '100%',
                  '& input': {
                    padding: '4px 0',
                    textAlign: 'center',
                    fontSize: '0.875rem'
                  }
                }
              }}
            />
          </Box>
        </Box>
      );
    } catch (error) {
      console.error('Error rendering seed card:', error);
      return null;
    }
  }, [inventory.seeds, handleQuantityChange]);

// Define renderSeedCardContent next
const renderSeedCardContent = useCallback((seed, currentCompactView = compactView) => {
  try {
    // Comprehensive null/undefined checks for seed and its properties
    if (typeof seed !== 'object') {
      return null;
    }

    // For compact view, we use the pre-filtered and sorted seed with rarity
    if (currentCompactView) {
      // Check for valid rarity in compact view
      if (!seed.rarity || typeof seed.rarity !== 'object' || !seed.rarity.type) {
        return null;
      }
      
      // Check if this seed-rarity combination should be shown
      if (seedRarityFilter !== 'All' && seed.rarity?.type !== seedRarityFilter) return null;
      if ((seed.count || 0) <= 0) return null;
      
      // Render the seed card with its rarity
      return renderSeedCard(seed, seed.rarity);
    } else {
      // Check for valid rarities array
      if (!Array.isArray(seed.rarities)) {
        return null;
      }
      
      // For regular view, filter rarities based on the current filter
      const filteredRarities = seed.rarities.filter((r) => {
        // Skip invalid rarities
        if (!r) {
          return false;
        }
        
        if (typeof r !== 'object') {
          return false;
        }
        
        if (!r.type) {
          return false;
        }
        
        // Apply filter if one is set
        if (seedRarityFilter !== 'All' && r.type !== seedRarityFilter) return false;
        return true;
      });
      
      if (filteredRarities.length === 0) {
        return null;
      }
      
      // Wrap the return in a try-catch to handle any rendering errors
      try {
        return (
          <Box sx={{ width: '100%' }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                textAlign: 'center',
                fontSize: '0.9rem',
                lineHeight: 1.2,
                px: 0.5,
                width: '100%',
                wordBreak: 'break-word',
                mb: 0.5,
              }}
            >
              {seed.name}
            </Typography>
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '8px', alignItems: 'center', justifyContent: 'center' }}>
              {filteredRarities.map((rarity) => {
                // Add a final check for rarity type before rendering
                if (!rarity || !rarity.type) {
                  return null;
                }
                
                return (
                  <Box
                    key={`${seed.id}-${rarity.type}`}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Tooltip
                      title={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                            {seed.name} ({rarity.type})
                          </Typography>
                          <Typography variant="body2">
                            Grow Time: {formatGrowTime(rarity.growTime)}
                          </Typography>
                          <Typography variant="body2">
                            Biopoints: {rarity.biopoints}
                          </Typography>
                          <Typography variant="body2">
                            BP/min: {calculateBpPerMinute(rarity.biopoints, rarity.growTime).toFixed(1)}
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <Box>
                        <RarityAvatar
                          src={`/images/seeds/${seed.id}/${rarity.type.toLowerCase()}/preview.png`}
                          alt={`${seed.name} ${rarity.type}`}
                          size={40}
                          sx={{ 
                            width: 40, 
                            height: 40, 
                            bgcolor: 'transparent', 
                            mb: '4px', 
                            '& img': { 
                              objectFit: 'contain', 
                              width: '100%', 
                              height: '100%' 
                            } 
                          }}
                        />
                      </Box>
                    </Tooltip>
                    <RarityBadge 
                      rarity={rarity.type}
                      size="small"
                    />
                    <SeedInput
                      id={`${seed.id}-${rarity.type}`}
                      value={
                        inventory.seeds[`${seed.id}-${rarity.type}`] === undefined ||
                        inventory.seeds[`${seed.id}-${rarity.type}`] === null ||
                        inventory.seeds[`${seed.id}-${rarity.type}`] === ''
                          ? 0
                          : inventory.seeds[`${seed.id}-${rarity.type}`]
                      }
                      onChange={(e) => handleQuantityChange('seeds', `${seed.id}-${rarity.type}`, e.target.value)}
                      onKeyDown={(e) => {
                        if (!/^[0-9]|Backspace|Delete|Tab|ArrowLeft|ArrowRight|ArrowUp|ArrowDown$/.test(e.key) &&
                          !(e.ctrlKey && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase()))) {
                          e.preventDefault();
                        }
                      }}
                      sx={{ width: 100, height: 40 }}
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      } catch (error) {
        return null;
      }
    }
  } catch (error) {
    return null;
  }
  }, [compactView, seedRarityFilter, inventory.seeds, handleQuantityChange, renderSeedCard]);

  // Separate food seeds from other seeds using the isFoodSeed property
  const { foodSeeds, otherSeeds } = useMemo(() => {
    const allSeeds = getSortedSeeds();
    return {
      foodSeeds: allSeeds.filter(seed => seed.isFoodSeed),
      otherSeeds: allSeeds.filter(seed => !seed.isFoodSeed)
    };
  }, []);

  // Filter seeds based on current view and filters
  const filterSeeds = useCallback((seeds) => {
    return seeds.filter(seed => {
      const hasVisibleRarities = seed.rarities.some(rarity => {
        if (seedRarityFilter !== 'All' && rarity.type !== seedRarityFilter) return false;
        if (compactView) {
          const count = inventory.seeds[`${seed.id}-${rarity.type}`] || 0;
          return count > 0;
        }
        return true;
      });
      return hasVisibleRarities;
    });
  }, [compactView, inventory.seeds, seedRarityFilter]);

  const visibleFoodSeeds = useMemo(() => filterSeeds(foodSeeds), [filterSeeds, foodSeeds]);
  const visibleOtherSeeds = useMemo(() => filterSeeds(otherSeeds), [filterSeeds, otherSeeds]);
  
  // Get all visible seed rarities for compact view, sorted by grow time (ascending) and then by rarity (descending)
  const allVisibleSeeds = useMemo(() => {
    if (!compactView) return [];
    
    // Create a flat array of all seed rarities with their parent seed info
    const allSeedRarities = [];
    
    // Process both food and other seeds
    [...visibleFoodSeeds, ...visibleOtherSeeds].forEach(seed => {
      seed.rarities.forEach(rarity => {
        const count = inventory.seeds[`${seed.id}-${rarity.type}`] || 0;
        // Only include if count > 0 or we're not in compact view
        if (count > 0 || !compactView) {
          allSeedRarities.push({
            ...seed,
            rarity,
            count,
            // Create a unique key for each seed-rarity combination
            key: `${seed.id}-${rarity.type}`
          });
        }
      });
    });
    
    // Define rarity order for sorting (lower number = higher priority)
    // Standard rarity order: Legendary > Epic > Rare > Uncommon > Common
    const rarityOrder = {
      'Legendary': 0,
      'Epic': 1,
      'Rare': 2,
      'Uncommon': 3,
      'Common': 4
    };
    
    // Sort by grow time (descending, longest first) and then by rarity (descending)
    const sorted = [...allSeedRarities].sort((a, b) => {
      // First sort by grow time (descending)
      if (a.rarity.growTime !== b.rarity.growTime) {
        return b.rarity.growTime - a.rarity.growTime;
      }

      // If same grow time, sort by rarity (higher rarity first)
      if (a.rarity.type !== b.rarity.type) {
        let aOrder, bOrder;

        // Force the correct order for debugging
        if (a.rarity.type === 'Legendary') {
          aOrder = 0;
        } else if (a.rarity.type === 'Epic') {
          aOrder = 1;
        } else if (a.rarity.type === 'Rare') {
          aOrder = 2;
        } else if (a.rarity.type === 'Uncommon') {
          aOrder = 3;
        } else if (a.rarity.type === 'Common') {
          aOrder = 4;
        } else {
          aOrder = rarityOrder[a.rarity.type] || 99;
        }

        if (b.rarity.type === 'Legendary') {
          bOrder = 0;
        } else if (b.rarity.type === 'Epic') {
          bOrder = 1;
        } else if (b.rarity.type === 'Rare') {
          bOrder = 2;
        } else if (b.rarity.type === 'Uncommon') {
          bOrder = 3;
        } else if (b.rarity.type === 'Common') {
          bOrder = 4;
        } else {
          bOrder = rarityOrder[b.rarity.type] || 99;
        }

        return aOrder - bOrder;
      }

      // If same rarity, sort by seed name for consistency
      return a.name.localeCompare(b.name);
    });

    return sorted;
  }, [compactView, visibleFoodSeeds, visibleOtherSeeds, inventory.seeds]);

  // Filter seeds based on the selected rarity
  const filterSeedsByRarity = useCallback((seeds) => {
    if (!Array.isArray(seeds)) {
      return [];
    }
    
    if (seedRarityFilter === 'All') return seeds;
    
    return seeds.filter(seed => {
      // Skip if seed or seed.rarity is invalid
      if (!seed) {
        return false;
      }
      
      // Handle both compact and regular view seed structures
      if (compactView) {
        // For compact view, we have a single rarity object
        if (seedRarityFilter !== 'All' && seed.rarity?.type !== seedRarityFilter) {
          return false;
        }
        return seed.rarity.type.toLowerCase() === seedRarityFilter.toLowerCase();
      } else {
        // For regular view, check all rarities
        if (!Array.isArray(seed.rarities) || seed.rarities.length === 0) {
          return false;
        }
        
        // Check if any of the rarities match the filter (case-insensitive)
        return seed.rarities.some(rarity => 
          rarity && 
          rarity.type && 
          rarity.type.toLowerCase() === seedRarityFilter.toLowerCase()
        );
      }
    });
  }, [seedRarityFilter, compactView]);

  // Render a section of seeds with a title
  // Handle rarity filter changes and view toggling
  const handleRarityFilterChange = useCallback((rarity) => {
    setSeedRarityFilter(rarity);
    if (rarity === 'All' && compactView) {
      toggleCompactView();
    }
  }, [compactView, toggleCompactView]);

  const renderSeedSection = useCallback((title, seeds) => {
    if (seeds.length === 0) return null;
    
    // In compact view, we'll handle all seeds together in the main return
    if (compactView) return null;
    
    // Filter seeds by rarity for regular view
    const filteredSeeds = filterSeedsByRarity(seeds);
    if (filteredSeeds.length === 0) return null;
    
    return (
      <Box sx={{ 
        mb: 4, 
        width: '100%',
        maxWidth: '1200px',
        mx: 'auto',
        boxSizing: 'border-box'
      }}>
        <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', width: '100%' }}>{title}</Typography>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          gap: 2,
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          {filteredSeeds.map((seed) => {
            if (!seed || typeof seed !== 'object') {
              return null;
            }
            
            const content = renderSeedCardContent(seed);
            const seedKey = seed.id ? 
              `${seed.id}-${seed.rarity?.type || 'no-rarity'}` : 
              `seed-${Math.random().toString(36).substr(2, 9)}`;
              
            return content ? (
              <Box key={seedKey} sx={{ width: '100%' }}>
                {content}
              </Box>
            ) : null;
          })}
        </Box>
      </Box>
    );
  }, [compactView, filterSeedsByRarity, renderSeedCardContent]);

  const renderSeedsTab = useCallback(() => {
    // Always show the filter controls at the top
    const filterControls = (
      <Box sx={{ 
        width: '1200px',
        maxWidth: '1200px',
        mx: 'auto',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        mb: 2,
        flexWrap: 'wrap',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        py: 1,
        boxSizing: 'border-box',
        backgroundColor: 'background.default',
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '1px',
          backgroundColor: 'divider',
        }
      }}>
        {RARITY_OPTIONS.map((opt) => {
          const color = opt === 'All' ? '#9e9e9e' : getRarityColor(opt);
          const isActive = seedRarityFilter === opt;
          return (
            <RarityBadge
              key={opt}
              rarity={opt}
              size="small"
              onClick={() => handleRarityFilterChange(opt)}
              sx={{
                cursor: 'pointer',
                borderWidth: isActive ? 2 : 1,
                backgroundColor: isActive ? `${color}33` : `${color}22`,
                flexShrink: 0,
                m: '2px !important',
                '& .MuiChip-label': {
                  px: 1.5,
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  overflow: 'visible',
                  textOverflow: 'clip',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }
              }}
            />
          );
        })}

        <RarityBadge
          key="compact-view"
          rarity="Compact"
          size="small"
          onClick={toggleCompactView}
          sx={{
            cursor: 'pointer',
            borderWidth: compactView ? 2 : 1,
            backgroundColor: compactView ? 'rgba(158, 158, 158, 0.2)' : 'transparent',
            color: 'text.primary',
            '&:hover': {
              backgroundColor: 'rgba(158, 158, 158, 0.1)'
            },
            flexShrink: 0,
            borderColor: '#9e9e9e',
            m: 0.5,
            '& .MuiChip-label': {
              px: 1.5,
              fontSize: '0.8rem',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              overflow: 'visible',
              textOverflow: 'clip',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }
          }}
          title={compactView ? 'Switch to regular view' : 'Switch to compact view'}
        />

        <RarityBadge
          rarity="Clear Seeds"
          size="small"
          onClick={() => {
            if (window.confirm('Clear all seed counts? This action cannot be undone.')) {
              handleClearSeeds();
            }
          }}
          sx={{
            cursor: 'pointer',
            borderColor: '#f44336',
            color: '#f44336',
            backgroundColor: '#f4433622',
            '&:hover': {
              backgroundColor: '#f4433633',
            },
            flexShrink: 0,
          }}
        />
      </Box>
    );

    if (compactView) {
      // Always show all seeds in compact view, regardless of filter
      const seedsToShow = allVisibleSeeds;
      return (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          width: '100%',
          maxWidth: '1200px',
          mx: 'auto',
          px: 2,
          boxSizing: 'border-box',
          flex: 1
        }}>
          {filterControls}
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: 2,
            width: '100%',
            maxWidth: '1200px',
            mx: 'auto',
            px: 2,
            boxSizing: 'border-box'
          }}>
            {seedsToShow.length > 0 ? (
              seedsToShow.map((seed) => {
                if (!seed || typeof seed !== 'object') {
                  return null;
                }
                
                const content = renderSeedCardContent(seed, true); // Pass true for compact view
                const seedKey = seed.id ? 
                  `${seed.id}-${seed.rarity?.type || 'no-rarity'}` : 
                  `seed-${Math.random().toString(36).substr(2, 9)}`;
                  
                return content ? (
                  <Box key={seedKey} sx={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 1,
                    bgcolor: 'transparent',
                    height: '100%',
                    boxSizing: 'border-box',
                    '&:hover': {
                      '& .seed-image': {
                        transform: 'scale(1.1)'
                      }
                    }
                  }}>
                    {content}
                  </Box>
                ) : null;
              })
            ) : (
              <Box sx={{ 
                gridColumn: '1 / -1', 
                textAlign: 'center', 
                py: 4,
                color: 'text.secondary'
              }}>
                No seeds available
              </Box>
            )}
          </Box>
        </Box>
      );
    }
    
    // Regular view with sections
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'flex-start', 
        alignItems: 'center', 
        minHeight: '100%',
        width: '100%',
        maxWidth: '1200px',
        mx: 'auto',
        px: 2,
        boxSizing: 'border-box'
      }}>
        {filterControls}
        <Box sx={{ 
          width: '100%',
          '& > *:not(:last-child)': {
            mb: 4
          }
        }}>
          {renderSeedSection('Food Seeds', visibleFoodSeeds)}
          {renderSeedSection('Other Seeds', visibleOtherSeeds)}
        </Box>
      </Box>
    );
  }, [renderSeedCardContent, seedRarityFilter, handleClearSeeds, visibleFoodSeeds, visibleOtherSeeds, compactView, allVisibleSeeds, renderSeedSection, toggleCompactView, handleRarityFilterChange]);

  // Debug border and size for beds section
  const renderBedsTab = useCallback((debugRef, size) => {
    const rarities = ['Common', 'Uncommon', 'Rare', 'Epic', 'Legendary'];
    return (
      <Box sx={{ 
        width: '100%', 
        maxWidth: 1200, 
        mx: 'auto', 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'flex-start', 
        mt: 0, 
        pt: 0,
        boxSizing: 'border-box'
      }}>
        <Box ref={debugRef} sx={{ 
          width: '100%', 
          maxWidth: 1200,
          m: 0, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          borderRadius: 2, 
          boxShadow: 0, 
          p: 2 
        }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, textAlign: 'center', fontSize: '0.9rem', lineHeight: 1.2, px: 0.5, width: '100%', wordBreak: 'break-word', mb: 0.5 }}
          >
            Beds
          </Typography>
          <Box sx={{ 
            width: '100%', 
            maxWidth: 1200,
            display: 'flex', 
            flexDirection: 'row', 
            flexWrap: 'wrap', 
            gap: '16px', 
            alignItems: 'flex-end', 
            justifyContent: 'center', 
            mb: 0.5 
          }}>
            {rarities.map((rarity, idx) => {
              const bed = BED_DATA.find(b => b.rarity.toLowerCase() === rarity.toLowerCase());
              return (
                <Box key={rarity} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 80, justifyContent: 'flex-end' }}>
                  <Tooltip title={bed?.name || rarity} arrow>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                      <RarityAvatar src={bed?.image} alt={bed?.name || rarity} size={40} />
                    </Box>
                  </Tooltip>
                  <RarityBadge rarity={rarity} size="small" />
                  <TextField
                    type="number"
                    value={inventory.beds[rarity] || 0}
                    onChange={(e) => handleQuantityChange('beds', rarity, e.target.value)}
                    inputProps={{
                      min: 0,
                      style: {
                        textAlign: 'center',
                        width: '100px',
                        padding: '4px 0px',
                        height: '24px',
                        boxSizing: 'border-box',
                        fontSize: '0.875rem'
                      }
                    }}
                    size="small"
                    variant="outlined"
                    sx={{
                      width: 100,
                      mt: 0,
                      height: '24px',
                      '& .MuiOutlinedInput-root': {
                        height: '24px',
                        padding: 0,
                        '& input': {
                          padding: '4px 0px',
                          textAlign: 'center',
                          width: '100px',
                          height: '24px',
                          '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0,
                          },
                          '&[type=number]': {
                            MozAppearance: 'textfield',
                          },
                        },
                      },
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    );
  }, [inventory.beds, handleQuantityChange]);

  const renderAnimalsTab = useCallback(() => {
    // Animal tab rendering logic

    // Animal types and their common image
    const animalTypes = [
      {
        key: 'birds',
        label: 'Birds',
        image: '/images/animals/birds/common/preview.png',
        rarities: ['Common', 'Uncommon', 'Rare', 'Epic', 'Legendary'],
        get ids() {
          return allAnimals.birds?.variants?.map(v => v?.id).filter(Boolean) || [];
        },
      },
      {
        key: 'cattle',
        label: 'Cattle',
        image: '/images/animals/cattle/common/preview.png',
        rarities: ['Common', 'Uncommon', 'Rare', 'Epic', 'Legendary'],
        get ids() {
          return allAnimals.cattle?.variants?.map(v => v?.id).filter(Boolean) || [];
        },
      },
      {
        key: 'fuzzies',
        label: 'Fuzzies',
        image: '/images/beds/fuzzies/common/preview.png',
        rarities: ['Common', 'Uncommon', 'Rare', 'Epic', 'Legendary'],
        get ids() {
          return allAnimals.fuzzies?.variants?.map(v => v?.id).filter(Boolean) || [];
        },
      },
      {
        key: 'piggies',
        label: 'Piggies',
        image: '/images/beds/piggies/common/preview.png',
        rarities: ['Common'],
        get ids() {
          return allAnimals.piggies?.variants?.map(v => v?.id).filter(Boolean) || [];
        },
      },
    ];

    const renderContent = () => {
      try {
        
        return (
          <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center', justifyContent: 'flex-start', mt: 0, pt: 0 }}>
        {animalTypes.map(animalType => {
          // Skip if animal type data is not available
          const variants = allAnimals[animalType.key]?.variants;
          if (!Array.isArray(variants) || variants.length === 0) {
            return null;
          }

          const firstVariant = variants[0];
          const rarities = firstVariant?.rarity;
          
          if (!Array.isArray(rarities) || rarities.length === 0) {
            return null;
          }

          return (
            <Box key={animalType.key} sx={{ width: 600, m: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRadius: 2, boxShadow: 0, p: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, textAlign: 'center', fontSize: '0.9rem', lineHeight: 1.2, px: 0.5, width: '100%', wordBreak: 'break-word', mb: 0.5 }}
              >
                {animalType.label}
              </Typography>
              
              <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '8px', alignItems: 'flex-end', justifyContent: 'center', mb: 0.5 }}>
                {rarities.map((rarity, idx) => {
                  // Skip if rarity data is invalid
                  if (!rarity || typeof rarity !== 'object' || !rarity.type) {
                    return null;
                  }
                  
                  const firstId = animalType.ids?.[0];
                  if (!firstId) {
                    console.log(`No first ID found for ${animalType.key}`);
                    return null;
                  }
                  
                  const animalKey = `${firstId}-${rarity.type}`;
                  if (!animalKey) {
                    return null;
                  }
                  
                  return (
                    <Box key={`${animalType.key}-${rarity.type}-${idx}`} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 80, justifyContent: 'flex-end', mb: 2 }}>
                      <Tooltip title={rarity.name || `Unknown ${animalType.label}`} arrow>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
                          <RarityAvatar 
                            src={animalType.key === 'fuzzies' || animalType.key === 'piggies' 
                              ? rarity.image.replace('/images/animals/', '/images/beds/')
                              : rarity.image} 
                            alt={rarity.name || `Unknown ${animalType.label}`} 
                            size={40}
                            sx={{ 
                              width: 40, 
                              height: 40,
                              bgcolor: 'transparent',
                              '& img': {
                                objectFit: 'contain',
                                width: '100%',
                                height: '100%'
                              }
                            }}
                          />
                        </Box>
                      </Tooltip>
                      <RarityBadge 
                        rarity={rarity.type} 
                        size="small" 
                        sx={{ mb: 0 }} 
                      />
                      <TextField
                        type="number"
                        value={inventory.animals?.[animalKey] || 0}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (animalKey) {
                            handleQuantityChange('animals', animalKey, value);
                          }
                        }}
                        size="small"
                        variant="outlined"
                        sx={{
                          width: '100px',
                          '& .MuiOutlinedInput-root': {
                            height: '24px',
                            '& input': {
                              textAlign: 'center',
                              padding: '2px 0',
                              height: '24px',
                              boxSizing: 'border-box',
                              fontSize: '0.875rem',
                              '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                                WebkitAppearance: 'none',
                                margin: 0,
                              },
                              '&[type=number]': {
                                MozAppearance: 'textfield',
                              },
                            },
                          },
                        }}
                        inputProps={{
                          min: 0
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
          })}
          </Box>
        );
      } catch (error) {
        return (
          <Box sx={{ color: 'error.main' }}>
            <Typography variant="h6">Error loading animal data</Typography>
            <Typography variant="body2">Please check the console for details.</Typography>
          </Box>
        );
      }
    };

    const content = renderContent();
    return content;
  }, [inventory.animals, handleQuantityChange]);

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: 'calc(100vh - 100px)', // Adjust for header and footer
      display: 'flex',
      flexDirection: 'column',
      pt: 5,
      pb: 8, // Add padding at the bottom
      boxSizing: 'border-box',
      '&::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
      },
      '&::-webkit-scrollbar-track': {
        background: 'rgba(0, 0, 0, 0.05)',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        borderRadius: '4px',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
        }
      },
    }}>
      <Box sx={{ 
        width: '100%',
        maxWidth: 1200,
        mx: 'auto',
        flex: '1 0 auto',
        display: 'flex',
        flexDirection: 'column' 
      }}>
        <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{ ...tabStyles.container, width: 1200 }}
            aria-label="inventory tabs"
          >
            {inventoryTabs.map((tab, index) => (
              <Tab
                key={tab.id}
                label={
                  <Box sx={tabStyles.content}>
                    <RarityAvatar
                      src={tab.image}
                      alt={tab.name}
                      size={40}
                      sx={avatarStyles.root}
                      aria-label={`View ${tab.name}`}
                    />
                    <Typography variant="caption" sx={tabStyles.label}>
                      {tab.name}
                    </Typography>
                  </Box>
                }
                sx={{ width: `${1200 / inventoryTabs.length}px`, ...tabStyles.root }}
              />
            ))}
          </Tabs>
          <Box sx={{ 
            width: '100%',
            flex: '1 0 auto',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            px: 0,
            '& > *': {
              flexShrink: 0 // Prevent content from shrinking
            }
          }}>
            {tabValue === 0 && renderSeedsTab()}
            {tabValue === 1 && renderBedsTab()}
            {tabValue === 2 && renderAnimalsTab()}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Inventory;